/**
 * =============================================================================
 * ALERT CONTAINER COMPONENT
 * =============================================================================
 * 
 * Main orchestrator component for the Alert/Todo system.
 * Manages the view state: Category list view vs Chat window view
 * 
 * VIEW STATES:
 * 1. Category View (default): Shows tabs with Pending/Done/Snoozed alerts
 * 2. Chat View: Shows pinned alert card with chat messages
 * 
 * COMPONENT STRUCTURE:
 * 
 * AlertContainer
 * ├── AlertCategoryView (home page list view)
 * │   └── AlertListItem (individual alert cards)
 * │
 * └── ChatWindow (expanded chat view)
 *     ├── PinnedAlertCard (reference point at top)
 *     ├── ChatLogEntry (action logs)
 *     ├── ActionDetailCard (action confirmation)
 *     └── IframeEditModal (edit form)
 * 
 * API INTEGRATION SUMMARY:
 * - AlertCategoryView: fetchAlertsByCategory() on mount
 * - ChatWindow.onActionClick: fetchActionDetails()
 * - ChatWindow.onConfirmAction: executeAlertAction()
 * - ChatWindow.onSnooze: snoozeAlert()
 * - ChatWindow.onDelete: deleteAlert()
 * - ChatWindow.onIframeConfirm: submitIframeEdit()
 */

import { Box, Stack } from '@mantine/core';
import { memo, useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import AlertCategoryView from './AlertCategoryView';
import AlertDetailLayout from './AlertDetailLayout';
import type { AlertItem } from './types';

interface AlertContainerProps {
    /** Current user name for logs */
    userName?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

const AlertContainer: React.FC<AlertContainerProps> = memo(({ userName = 'You' }) => {
    // =========================================================================
    // STATE
    // =========================================================================
    
    /** Currently selected alert (null = show category view) */
    const [selectedAlert, setSelectedAlert] = useState<AlertItem | null>(null);
    
    /** Key to force refresh category view */
    const [refreshKey, setRefreshKey] = useState(0);

    // =========================================================================
    // HANDLERS
    // =========================================================================

    /**
     * Handle alert click from category view
     * Opens the chat window with selected alert
     */
    const handleAlertClick = useCallback((alert: AlertItem) => {
        setSelectedAlert(alert);
    }, []);

    /**
     * Handle back button in chat view
     * Returns to category view
     */
    const handleBack = useCallback(() => {
        setSelectedAlert(null);
    }, []);

    /**
     * Handle alert updates (delete, complete, snooze)
     * Refreshes the category view
     */
    const handleAlertUpdated = useCallback((alertId: string, action: 'deleted' | 'completed' | 'snoozed' | 'ignored') => {
        console.log(`[AlertContainer] Alert ${alertId} ${action}`);
        
        // Force refresh the category view
        setRefreshKey((prev) => prev + 1);
        
        // If action was delete or ignored, close chat window
        if (action === 'deleted' || action === 'ignored') {
            setSelectedAlert(null);
        }
    }, []);

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
        <Box className="flex h-full w-full flex-1 flex-col overflow-hidden">
            <AnimatePresence mode="wait">
                {selectedAlert ? (
                    // =========================================================
                    // DETAIL VIEW (3-pane layout)
                    // Center: Alert flow & logs | Right: AI Chat
                    // =========================================================
                    <motion.div
                        key="detail"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="h-full"
                    >
                        <AlertDetailLayout
                            alert={selectedAlert}
                            onBack={handleBack}
                            onAlertUpdated={handleAlertUpdated}
                            currentUser={{ name: userName }}
                        />
                    </motion.div>
                ) : (
                    // =========================================================
                    // CATEGORY VIEW
                    // Shows tabs: Pending | Done | Snoozed
                    // =========================================================
                    <motion.div
                        key={`categories-${refreshKey}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                        className="h-full overflow-hidden"
                    >
                        <Stack className="h-full gap-0 p-4">
                            <AlertCategoryView onAlertClick={handleAlertClick} />
                        </Stack>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
});

AlertContainer.displayName = 'AlertContainer';

export default AlertContainer;
